export const argumentErrors = {
  ARGUMENTS_INVALID: {
    status: 400,
    code: 'ARGUMENTS_INVALID',
    title: 'Invalid Arguments',
    subtitle: 'INVALID ARGUMENTS',
    description: 'One or more arguments provided are invalid for this step. Please review the input data and ensure that all arguments meet the required format and value types.',
    roles: {
      admin: {
        description: 'The provided arguments do not match the expected format or types for this step. This can occur if the data type (e.g., string, number, etc.) or format does not align with the API or action requirements.',
        about: {
          title: 'More on this error',
          description: 'Arguments must be provided in the correct format and data type as expected by the API or action being executed. Common issues include mismatched data types or formatting inconsistencies that prevent proper processing.',
          link: { text: 'Learn More', url: 'https://mindcloud.co/docs/gravity/gravity-app-builder/argument-fundamentals' }
        }
      }
    },
    component: 'DefaultErrorPage'
  },
  ARGUMENTS_MISSING: {
    status: 400,
    code: 'ARGUMENTS_MISSING',
    title: 'Missing Required Arguments',
    subtitle: 'MISSING ARGUMENTS',
    description: 'One or more required arguments were not provided for this step. Please ensure all necessary fields are completed before proceeding. You can view the arguments that were sent using the Payload tab.',
    roles: {
      admin: {
        description: 'One or more required arguments are missing from the request. This error typically occurs when essential fields are not completed, leading to an incomplete request to the API or action.',
        about: {
          title: 'More on this error',
          description: 'Required arguments must be provided to fulfill API or action requests. If required fields are not supplied, the system cannot proceed with the operation. Review the argument setup and ensure all mandatory fields are present.',
          link: { text: 'Learn More', url: 'https://mindcloud.co/docs/gravity/gravity-app-builder/argument-fundamentals' }
        }
      }
    },
    component: 'DefaultErrorPage'
  },
  ARGUMENTS_JSON_VALIDATION: {
    status: 500,
    code: 'ARGUMENTS_JSON_VALIDATION',
    title: 'Argument Failed JSON Validation',
    subtitle: 'FAILED JSON VALIDATION',
    description: 'One or more arguments failed JSON validation. This could be due to incorrect formatting or invalid data types. Please review the argument structure and use the Payload tab to verify the data sent.',
    roles: {
      admin: {
        description: 'The argument data could not be validated as proper JSON. This is usually due to incorrect JSON formatting or invalid data types being passed. Review the JSON structure for the arguments and ensure proper syntax.',
        about: {
          title: 'More on this error',
          description: 'JSON validation errors occur when the argument data does not conform to the required JSON format. Ensure that the argument values are correctly structured and that no syntax errors are present.',
          link: { text: 'Learn More', url: 'https://mindcloud.co/docs/gravity/gravity-app-builder/argument-fundamentals' }
        }
      }
    },
    component: 'DefaultErrorPage'
  },
  ARGUMENTS_DEFAULT: {
    status: 500,
    code: 'ARGUMENTS_DEFAULT',
    title: 'Argument Validation Error',
    subtitle: 'ARGUMENT VALIDATION ERROR',
    description: 'There was an issue validating one or more arguments for this step. Please review the argument data and refer to the Payload tab for more details on what was sent.',
    roles: {
      admin: {
        description: 'The argument validation failed due to one or more incorrect or improperly formatted arguments. This could involve incorrect data types, missing required fields, or improper structure.',
        about: {
          title: 'More on this error',
          description: 'Argument validation ensures that the provided data conforms to the expected structure and format. Validation failures can occur if arguments are not provided, are incorrectly typed, or do not meet the expected format for this step.',
          link: { text: 'Learn More', url: 'https://mindcloud.co/docs/gravity/gravity-app-builder/argument-fundamentals' }
        }
      }
    },
    component: 'DefaultErrorPage'
  },
  INVALID_ZIP_FILE: {
    status: 400,
    code: 'INVALID_ZIP_FILE',
    title: 'Invalid Zip File',
    subtitle: 'INVALID ZIP FILE',
    description: 'The provided file is not a valid zip file. Please ensure that the file is a valid zip file before proceeding.',
    roles: {
      admin: {
        description: 'The provided file is not a valid zip file. This error occurs when the file is not a valid zip file or does not contain the expected zip structure.',
        about: {
          title: 'More on this error',
          description: 'Zip files must be properly formatted and contain the required zip structure. If the file is not a valid zip file, it cannot be processed by the system.',
          link: { text: 'Learn More', url: 'https://mindcloud.co/docs/gravity/gravity-app-builder/argument-fundamentals' }
        }
      }
    },
    component: 'DefaultErrorPage'
  },
  READ_STREAM_FAILED: {
    status: 500,
    code: 'READ_STREAM_FAILED',
    title: 'Zip Read Stream Failed',
    subtitle: 'READ STREAM FAILED',
    description: 'An error occurred while reading a file from the zip archive. This may be due to a corrupt entry or an unexpected I/O error.',
    roles: {
      admin: {
        description: 'Reading a specific entry from the zip failed. This can happen due to file corruption, unexpected I/O issues, or a malformed archive entry.',
        about: {
          title: 'More on this error',
          description: 'Zip read stream failures typically indicate a problem with the archive entry or the I/O layer while streaming data.',
          link: { text: 'Learn More', url: 'https://mindcloud.co/docs/gravity/gravity-app-builder/argument-fundamentals' }
        }
      }
    },
    component: 'DefaultErrorPage'
  },
  ZIP_READ_ERROR: {
    status: 500,
    code: 'ZIP_READ_ERROR',
    title: 'Zip Read Error',
    subtitle: 'ZIP READ ERROR',
    description: 'A general error occurred while reading the zip archive. The operation could not be completed.',
    roles: {
      admin: {
        description: 'A non-specific error arose while reading from the zip archive. This may be caused by corrupt data or an unexpected I/O condition.',
        about: {
          title: 'More on this error',
          description: 'General zip read errors often indicate corrupted archives or environmental I/O issues during processing.',
          link: { text: 'Learn More', url: 'https://mindcloud.co/docs/gravity/gravity-app-builder/argument-fundamentals' }
        }
      }
    },
    component: 'DefaultErrorPage'
  },
  ZIP_WRITE_ERROR: {
    status: 500,
    code: 'ZIP_WRITE_ERROR',
    title: 'Zip Write Error',
    subtitle: 'ZIP WRITE ERROR',
    description: 'An error occurred while writing the zip file. The archive may not have been created successfully.',
    roles: {
      admin: {
        description: 'The system encountered an error emitting zip output data. This may be due to resource constraints or a downstream I/O failure.',
        about: {
          title: 'More on this error',
          description: 'Zip write errors usually indicate a problem while streaming output, such as I/O failures or memory pressure.',
          link: { text: 'Learn More', url: 'https://mindcloud.co/docs/gravity/gravity-app-builder/argument-fundamentals' }
        }
      }
    },
    component: 'DefaultErrorPage'
  },
  INVALID_FILE_DATA: {
    status: 400,
    code: 'INVALID_FILE_DATA',
    title: 'Invalid File Data',
    subtitle: 'INVALID FILE DATA',
    description: 'One or more files contained unsupported or malformed data. Ensure each file is a valid base64 string, ArrayBuffer, Buffer, or TypedArray.',
    roles: {
      admin: {
        description: 'File data provided to build the zip was not in a supported format. Expected base64 string, ArrayBuffer, Buffer, or TypedArray.',
        about: {
          title: 'More on this error',
          description: 'Input validation failed for one or more files. Verify the data format and that it matches the expected types.',
          link: { text: 'Learn More', url: 'https://mindcloud.co/docs/gravity/gravity-app-builder/argument-fundamentals' }
        }
      }
    },
    component: 'DefaultErrorPage'
  },
  ZIP_CREATE_ERROR: {
    status: 500,
    code: 'ZIP_CREATE_ERROR',
    title: 'Zip Create Error',
    subtitle: 'ZIP CREATE ERROR',
    description: 'An unexpected error occurred while creating the zip archive. The operation was not completed.',
    roles: {
      admin: {
        description: 'A general error occurred during zip creation. This may be due to unexpected runtime conditions or resource limits.',
        about: {
          title: 'More on this error',
          description: 'Zip creation errors indicate that the process failed unexpectedly. Check input data and system logs for details.',
          link: { text: 'Learn More', url: 'https://mindcloud.co/docs/gravity/gravity-app-builder/argument-fundamentals' }
        }
      }
    },
    component: 'DefaultErrorPage'
  },
  PGP_INVALID_PUBLIC_KEY: {
    status: 400,
    code: 'PGP_INVALID_PUBLIC_KEY',
    title: 'Invalid PGP Public Key',
    subtitle: 'INVALID PGP KEY',
    description: 'The provided public key is not valid for encryption. Ensure it includes BEGIN/END PGP PUBLIC KEY BLOCK headers and is an encryption-capable key (e.g., RSA 2048+).',
    roles: {
      admin: {
        description: 'The PGP public key was not valid armored text, or is not encryption-capable (e.g., DSA signing-only or too weak). Provide an encryption-capable key with proper headers and real newlines.',
        about: {
          title: 'About This Error',
          description: 'PGP keys must be armored with correct headers/footers and must include an encryption-capable subkey. Copy the key exactly as exported from your PGP tool.',
          link: { text: 'Learn More', url: 'https://en.wikipedia.org/wiki/Pretty_Good_Privacy' }
        }
      }
    },
    component: 'DefaultErrorPage'
  },
  PGP_ENCRYPTION_ERROR: {
    status: 500,
    code: 'PGP_ENCRYPTION_ERROR',
    title: 'PGP Encryption Failed',
    subtitle: 'PGP ENCRYPTION ERROR',
    description: 'The content could not be encrypted with the provided public key. Check the key and the content, then try again.',
    roles: {
      admin: {
        description: 'OpenPGP.js failed while reading the key or encrypting the content, and the failure did not match a known key problem (those report as PGP_INVALID_PUBLIC_KEY). The library message replaces this text when available.',
        about: {
          title: 'About This Error',
          description: 'Encryption runs in-process with OpenPGP.js using GnuPG-compatible key acceptance (RSA of any size, keys without key-flags subpackets). Retry the step; if it keeps failing, capture the library message and the tool that exported the key.',
          link: { text: 'Learn More', url: 'https://en.wikipedia.org/wiki/Pretty_Good_Privacy' }
        }
      }
    },
    component: 'DefaultErrorPage'
  }
};
